import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import aminoLogo from "../amino-logo.png";

// If a company's login URL ever changes, a printed QR only stays valid if the
// URL is stable. Prefer pointing this QR at a permanent URL on our own domain
// that redirects to the current login.

const PRINT_SIZE = 1200;
const LOGO_ASPECT = 373 / 1024;
const LOGO_WIDTH_RATIO = 0.22;

export interface CompanyQRProps {
  url: string;
  fileName: string;
  size?: number;
  logoSrc?: string;
}

export function CompanyQR({
  url,
  fileName,
  size = 180,
  logoSrc = aminoLogo,
}: CompanyQRProps) {
  const [logoDataUri, setLogoDataUri] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAsDataUri(logoSrc).then(
      (uri) => {
        if (!cancelled) setLogoDataUri(uri);
      },
      () => {
        if (!cancelled) setLogoDataUri(logoSrc);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [logoSrc]);

  const imageSettings = (logoSize: number) =>
    logoDataUri
      ? {
          src: logoDataUri,
          width: Math.round(logoSize * LOGO_WIDTH_RATIO),
          height: Math.round(logoSize * LOGO_WIDTH_RATIO * LOGO_ASPECT),
          excavate: true,
        }
      : undefined;

  const downloadPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, `${fileName}.png`);
      URL.revokeObjectURL(objectUrl);
    }, "image/png");
  };

  const downloadSvg = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    const blob = new Blob(
      ['<?xml version="1.0" encoding="UTF-8"?>\n', serialized],
      { type: "image/svg+xml;charset=utf-8" },
    );
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, `${fileName}.svg`);
    URL.revokeObjectURL(objectUrl);
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="bg-white p-3 rounded">
        <QRCodeSVG
          ref={svgRef}
          value={url}
          size={size}
          level="H"
          fgColor="#000000"
          bgColor="#ffffff"
          marginSize={2}
          imageSettings={imageSettings(size)}
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={downloadPng}>
          <Download className="h-3 w-3" />
          PNG
        </Button>
        <Button size="sm" variant="ghost" onClick={downloadSvg}>
          <Download className="h-3 w-3" />
          SVG
        </Button>
      </div>
      <div
        className="absolute -left-[9999px] pointer-events-none"
        aria-hidden="true"
      >
        <QRCodeCanvas
          ref={canvasRef}
          value={url}
          size={PRINT_SIZE}
          level="H"
          fgColor="#000000"
          bgColor="#ffffff"
          marginSize={2}
          imageSettings={imageSettings(PRINT_SIZE)}
        />
      </div>
    </div>
  );
}

async function fetchAsDataUri(src: string): Promise<string> {
  const res = await fetch(src);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function triggerDownload(href: string, name: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
